import React from 'react';
import { useQuery, useMutation } from '@apollo/react-hooks';
import PropTypes from 'prop-types';

import AudioPlayer from '../components/AudioPlayer';
import Layout from '../components/Layout';
import Container from '../components/Container';
import Notification from '../components/Notification';
import TextContent from '../components/TextContent';
import Section from '../components/Section';
import FooterBar from '../components/FooterBar';
import Grid from '../components/Grid';
import GridItem from '../components/GridItem';
import Table from '../components/Table';

import { GET_ACTIVITY_DETAILS } from '../graph/activities';
import { GET_CMS_CONTENT } from '../graph/cms';
import { GET_BELIEFS, ADD_BELIEF, DELETE_BELIEF } from '../graph/beliefs';

import extractGraphQLError from '../utils/extractGraphQLError';
import {
  extractPrismicString,
  extractPrismicContent,
  hasContent,
} from '../utils/prismic';
import { validate, requiredString } from '../utils/validate';
import useTrackActivityGA from '../utils/useTrackActivityGA';

import { activities, ACTIVITY_STATUS } from '../config/levels';

import { ReactComponent as RemoveIcon } from '../assets/remove-icon.svg';

const formValidationSchema = validate({
  title: requiredString,
  description1: requiredString,
});

const formInitialValues = {
  title: '',
  description1: '',
};

const formFields = [
  {
    id: 'title',
    name: 'title',
    label: 'Limiting Belief',
    type: 'textarea',
    placeholder: 'Enter a limiting belief...',
  },
  {
    id: 'description1',
    name: 'description1',
    label: 'Why you believe it',
    type: 'textarea',
    placeholder: 'Why do you believe it?',
  },
  {
    id: 'description2',
    name: 'description2',
    label: 'How it stops you	',
    type: 'textarea',
    placeholder: 'How does it stops you?',
  },
];

function ActivityBelief({ activityId }) {
  const { data: cmsData } = useQuery(GET_CMS_CONTENT, {
    fetchPolicy: 'cache-only',
  });

  const { data, loading } = useQuery(GET_ACTIVITY_DETAILS, {
    variables: { id: activityId },
  });

  const { loading: loadingBeliefs, data: dataBeliefs } = useQuery(GET_BELIEFS, {
    variables: { activityId },
  });

  const filteredBeliefs = dataBeliefs?.beliefs?.filter(
    belief => belief?.type === 'LIMITING',
  );

  const isBeliefValid = filteredBeliefs?.length > 0;

  const [
    handleAddBelief,
    { loading: loadingAddBelief, error: errorAddBelief },
  ] = useMutation(ADD_BELIEF, {
    refetchQueries: ['Beliefs'],
  });

  const handleNewBelief = formData => {
    handleAddBelief({
      variables: {
        activityId,
        type: 'LIMITING',
        ...formData,
      },
    });
  };

  const [
    handleDeleteBelief,
    { loading: loadingDeleteBelief, error: errorDeleteBelief },
  ] = useMutation(DELETE_BELIEF, {
    refetchQueries: ['Beliefs'],
  });

  const activityType = data?.activity?.type;

  useTrackActivityGA(data?.activity?.type, '1');

  const { level } = activities[activityType] || {};

  const {
    title,
    sub_title: subTitle,
    instructions,
    audio_instructions_1: audioInstructions,
    help_text_1: helpText,
    short_instructions_1: shortInstructions,
    step_label_1: stepLabel,
  } =
    cmsData?.cmsContent?.activity?.find(
      activity =>
        activity.id ===
        (cmsData?.user?.currentLevel === 4 &&
        data?.activity?.status === ACTIVITY_STATUS.COMPLETED
          ? `ongoing_${activityType}`
          : activityType),
    ) ||
    cmsData?.cmsContent?.activity?.find(
      activity => activity.id === activityType,
    ) ||
    {};

  return (
    <Layout
      title={`${extractPrismicString(title)}`}
      loading={loading || loadingBeliefs}
      hasFooterBar
    >
      <Container>
        <Section>
          {!!(hasContent(shortInstructions) || hasContent(helpText)) && (
            <Grid>
              <GridItem
                alignTop
                width={
                  audioInstructions?.url || hasContent(helpText) ? 60 : 100
                }
              >
                <TextContent redh6 smallMargin>
                  {extractPrismicContent(shortInstructions)}
                </TextContent>
              </GridItem>
              {!!(audioInstructions?.url || hasContent(helpText)) && (
                <GridItem width={40} alignTop>
                  <Notification showUser noMarginBottom>
                    {extractPrismicContent(helpText)}

                    {!!audioInstructions?.url && (
                      <AudioPlayer fileUrl={audioInstructions?.url} />
                    )}
                  </Notification>
                </GridItem>
              )}
            </Grid>
          )}
        </Section>

        <TextContent redh6 smallMargin>
          <h6>Step 1</h6>
          {extractPrismicContent(stepLabel)}
        </TextContent>

        <Table
          cols={[
            { id: 'title', value: 'Limiting Belief' },
            { id: 'description1', value: 'Why you believe it' },
            { id: 'description2', value: 'How it stops you' },
          ]}
          data={filteredBeliefs}
          actions={[
            {
              id: 'remove',
              text: 'remove',
              icon: <RemoveIcon />,
              onClick: handleDeleteBelief,
              loading: loadingDeleteBelief,
            },
          ]}
          form={{
            fields: formFields,
            loading: loadingAddBelief,
            validationSchema: formValidationSchema,
            initialValues: formInitialValues,
            onSubmit: handleNewBelief,
          }}
        />

        {!!(errorAddBelief || errorDeleteBelief) && (
          <Notification type="red">
            {extractGraphQLError(errorAddBelief || errorDeleteBelief)}
          </Notification>
        )}
      </Container>

      <FooterBar
        title={extractPrismicString(title)}
        subTitle={extractPrismicString(subTitle)}
        primaryButton={{
          label: 'Next: Growth Mindset Response',
          to: `/activities/${activityId}/limiting_beliefs/2`,
          disabled: !isBeliefValid,
          level,
        }}
        secondaryButton={{
          label: hasContent(instructions)
            ? 'Back to instructions'
            : 'Back to introduction',
          to: `/activities/${activityId}/${
            hasContent(instructions) ? 'instructions' : 'introduction'
          }`,
        }}
      />
    </Layout>
  );
}

ActivityBelief.propTypes = {
  activityId: PropTypes.string,
};

ActivityBelief.defaultProps = {
  activityId: null,
};

export default ActivityBelief;
